import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from "../../utils/getImageUrl";

const ProgramCards = ({
  programs,
  basePath = "/course",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const navigate = useNavigate();

  // Map program headings to actual routes defined in App.js
  const headingToRoute = {
    "Information Technology": "/course/information-technology",
    "Cybersecurity": "/course/cyber-security",
    "Business Administration": "/course/business-administration",
    "Travel and Tourism": "/course/travel-tourism-management",
  };

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const toggleExpand = (programId) => {
    setExpandedCardId((prev) => (prev === programId ? null : programId));
  };

  const displayPrograms = Array.isArray(programs) ? programs : [];

  if (programs === null) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-10 mt-8 mb-32 justify-center items-start min-[1280px]:max-[1403px]:!gap-6 min-[1024px]:max-[1535px]:!gap-6 min-[1024px]:max-[1535px]:!mb-24 min-[1024px]:max-[1535px]:!mt-6">
        <div className="col-span-full text-center text-gray-500">Loading programs...</div>
      </div>
    );
  }

  if (displayPrograms.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-10 mt-8 mb-32 justify-center items-start min-[1280px]:max-[1403px]:!gap-6 min-[1024px]:max-[1535px]:!gap-6 min-[1024px]:max-[1535px]:!mb-24 min-[1024px]:max-[1535px]:!mt-6">
        <div className="col-span-full text-center text-gray-500">No programs available right now.</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-10 mt-8 mb-32 justify-center items-start min-[1280px]:max-[1403px]:!gap-6">
      {displayPrograms.map((program, index) => {
        const isExpanded = expandedCardId === program.id;
        const controlsId = `certifications-${program.id}`;
        return (
          <div
            key={program.id}
            className={`
            border rounded-xl overflow-hidden shadow-lg w-full mx-auto max-w-[340px]
            transform transition-all duration-700 ease-out bg-white
            hover:shadow-2xl hover:-translate-y-3 hover:scale-[1.02]
            group
            ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }
          `}
            style={{
              transitionDelay: `${index * 150}ms`,
            }}
          >
            <div className="relative overflow-hidden">
              <img
                src={getImageUrl(program.image)}
                alt={program.heading}
                className="w-full h-[220px] min-[1024px]:max-[1535px]:!h-[190px] object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                onError={(e) => {
                  // Fallback if image fails to load
                  console.error('Failed to load program image:', program.image);
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%23ddd" width="400" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E'
                }}
              />
              <span className="block border-b-4 border-gold w-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out origin-left"></span>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 ease-out"></div>
            </div>
            <div className="p-6 text-left min-[1024px]:max-[1535px]:!p-4 min-[1280px]:max-[1403px]:!p-3.5">
              <h3 className="font-bold text-[1.25rem] mb-2 text-custom-pink tracking-normal min-[1024px]:max-[1535px]:!text-base">
                {program.heading}
              </h3>
              <p className="mb-4 text-[0.95rem] text-gray-800 min-[1024px]:max-[1535px]:!text-sm min-[1024px]:max-[1535px]:!mb-3">
                {program.introduction}
              </p>
              <div className="flex items-center gap-3 flex-wrap min-[1280px]:max-[1403px]:w-full min-[1280px]:max-[1403px]:justify-between min-[1280px]:max-[1403px]:gap-0 min-[1280px]:max-[1403px]:flex-nowrap">
                <button
                  onClick={() => toggleExpand(program.id)}
                  aria-expanded={isExpanded}
                  aria-controls={controlsId}
                  className={`
                    bg-custom-pink text-xs sm:text-sm text-white px-3 sm:px-5 py-2 rounded-full
                    border border-transparent
                    transition-all duration-300 ease-out
                    hover:bg-white hover:text-custom-pink hover:border-custom-pink
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-pink
                    hover:scale-105 hover:shadow-lg
                    whitespace-nowrap shrink-0
                    min-[1280px]:max-[1403px]:!text-[0.72rem] min-[1280px]:max-[1403px]:!px-2.5 min-[1280px]:max-[1403px]:!py-2
                    min-[1280px]:max-[1403px]:hover:!scale-100
                  `}
                >
                  {isExpanded ? "Hide Certifications" : program.buttonText}
                </button>
                <button
                  onClick={() => {
                    // Use learnMoreLink from DB if available, otherwise fall back to slug logic
                    let target;
                    if (program.learnMoreLink) {
                      target = program.learnMoreLink;
                    } else {
                      const slug = program.heading
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)/g, "");
                      target = headingToRoute[program.heading] || `${basePath}/${slug}`;
                    }
                    navigate(target);
                  }}
                  className="text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full border border-gray-300 text-gray-700 hover:border-gold hover:text-gold transition-colors whitespace-nowrap shrink-0 min-[1280px]:max-[1403px]:!text-[0.72rem] min-[1280px]:max-[1403px]:!px-2.5 min-[1280px]:max-[1403px]:!py-2"
                >
                  Learn More
                </button>
              </div>

              <div
                id={controlsId}
                role="region"
                aria-label={`${program.heading} certifications`}
                className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out ${
                  isExpanded ? "max-h-80 opacity-100 mt-4" : "max-h-0 opacity-0"
                }`}
              >
                <ul className="list-disc pl-5 space-y-2 text-gray-800">
                  {program.certifications?.map((cert, idx) => (
                    <li key={idx} className="leading-relaxed">
                      {cert}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProgramCards;
